import { Controller, Control, FieldErrors } from "react-hook-form";
import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import { useLocation } from "react-router-dom";
import TagsManagementMultidropdown from "../../../../../common/components/select/LocationTagSelect/TagsManagementMultidropdown";
import { useLazyGetPaginatedAllTagsByCustomer } from "../../../../system-settings/tags/hooks/useLazyGetPaginatedAllTagsByCustomer";
import { useAuthenticatedUser } from "../../../../../common/hooks/useAuthenticatedUser";
import { NonAdminRoles } from "../../../../../common/variables/tabsSettings";
import { RouteEnum } from "../../../../../common/models/enums";
import { ILocationForm } from "../variables/locationData";
import {
  LON_LAT_NAME_PATTERN,
  SIMPLE_NAME_PATTERN,
} from "../../../../../common/variables/common";

const timeZones = [
  { label: "Pacific Time (PT)", value: "Pacific Time (PT)" },
  { label: "Mountain Time (MT)", value: "Mountain Time (MT)" },
  { label: "Central Time (CT)", value: "Central Time (CT)" },
  { label: "Eastern Time (ET)", value: "Eastern Time (ET)" },
  { label: "Japan Standard Time (JST)", value: "Japan Standard Time (JST)" },
];

interface ILocationFormProps {
  control: Control<ILocationForm>;
  errors: FieldErrors<ILocationForm>;
  fullWidthInput?: boolean;
}

const LocationForm = ({
  fullWidthInput,
  errors,
  control,
}: ILocationFormProps): JSX.Element => {
  const location = useLocation();
  const isCreatePage = location.pathname === RouteEnum.CreateLocation;
  const { role } = useAuthenticatedUser();
  const isItNonAdminRole = NonAdminRoles.includes(role);

  const { tags } = useLazyGetPaginatedAllTagsByCustomer();
  const sm = fullWidthInput ? 12 : 6;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={sm}>
        <Controller
          name="locationName"
          control={control}
          rules={{
            required: "This field is required",
            pattern: {
              value: SIMPLE_NAME_PATTERN,
              message: "Valid characters: A-Z, a-z, 0-9, -, space",
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              sx={{
                height: "58px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
              label="Location name"
              fullWidth
              disabled={isItNonAdminRole}
              error={!!errors.locationName}
              helperText={errors.locationName?.message ?? ""}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={sm}>
        <Controller
          name="referenceId"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              sx={{
                height: "58px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
              label="Reference ID (optional)"
              fullWidth
              disabled={isItNonAdminRole}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={sm}>
        <Controller
          name="latitude"
          control={control}
          rules={{
            required: isCreatePage ? "This field is required" : false,
            pattern: {
              value: LON_LAT_NAME_PATTERN,
              message: "Only digits and optional negative sign allowed",
            },
            validate: value =>
              (parseFloat(value) >= -90 && parseFloat(value) <= 90) ||
              "Latitude must be between -90 and 90",
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Latitude"
              fullWidth
              sx={{
                height: "58px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
              disabled={isItNonAdminRole}
              error={!!errors.latitude}
              helperText={errors.latitude?.message ?? ""}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={sm}>
        <Controller
          name="longitude"
          control={control}
          rules={{
            required: isCreatePage ? "This field is required" : false,
            pattern: {
              value: LON_LAT_NAME_PATTERN,
              message: "Valid characters: 0-9, optional sign and dot",
            },
            validate: value =>
              (parseFloat(value) >= -180 && parseFloat(value) <= 180) ||
              "Longitude must be between -180 and 180",
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Longitude"
              fullWidth
              sx={{
                height: "58px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
              disabled={isItNonAdminRole}
              error={!!errors.longitude}
              helperText={errors.longitude?.message ?? ""}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={sm}>
        <Controller
          name="timeZone"
          control={control}
          rules={{ required: "This field is required" }}
          render={({ field }) => (
            <FormControl
              sx={{
                height: "58px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
              fullWidth
              error={!!errors.timeZone}
            >
              <InputLabel id="timeZone-label">Select timezone</InputLabel>
              <Select
                {...field}
                value={field.value ?? ""}
                labelId="timeZone-label"
                label="Select timezone"
                disabled={isItNonAdminRole}
              >
                {timeZones.map(timezone => (
                  <MenuItem key={timezone.value} value={timezone.value}>
                    {timezone.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.timeZone?.message ?? ""}</FormHelperText>
            </FormControl>
          )}
        />
      </Grid>

      <Grid item xs={12} sm={sm}>
        <Controller
          name="tags"
          control={control}
          render={({ field }) => (
            <TagsManagementMultidropdown
              entityTags={field.value ?? []}
              allTags={tags}
              onChange={(_event, newValue: string[]) => {
                const selectedTags = tags.filter(tag =>
                  newValue.includes(tag.tagName)
                );

                field.onChange(selectedTags);
              }}
            />
          )}
        />
      </Grid>
    </Grid>
  );
};

export default LocationForm;
